<template>
  <div class="min-h-screen bg-gray-50">
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-8">
        <a href="/billing/dashboard" class="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-500">
          <svg class="mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </a>
        <h1 class="mt-4 text-2xl font-bold text-gray-900">Billing Settings</h1>
        <p class="mt-2 text-sm text-gray-600">Configure your billing settings and fee structure.</p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="bg-white shadow rounded-lg p-6 text-center">
        <svg class="animate-spin h-8 w-8 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="mt-2 text-gray-600">Loading settings...</p>
      </div>

      <!-- Error State -->
      <div v-if="loadError" class="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">{{ loadError }}</h3>
          </div>
        </div>
      </div>

      <!-- Save Message -->
      <div v-if="saveMessage" class="mb-4 rounded-md p-4 border" :class="saveMessage.includes('Error') ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'">
        <p class="text-sm font-medium" :class="saveMessage.includes('Error') ? 'text-red-800' : 'text-green-800'">{{ saveMessage }}</p>
      </div>

      <!-- Main Form -->
      <div v-if="!isLoading" class="bg-white shadow rounded-lg">
        <form @submit.prevent="submitForm" class="space-y-8 p-6">
          <!-- Basic Settings -->
          <div class="space-y-6">
            <div>
              <h2 class="text-lg font-medium text-gray-900">Basic Settings</h2>
              <p class="mt-1 text-sm text-gray-500">Configure your basic billing information.</p>
            </div>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="billingYear" class="block text-sm font-medium text-gray-700">Billing Year</label>
                <input type="number" id="billingYear" v-model="billingYear" placeholder="YYYY" min="2000" max="2099" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
              </div>
              <div>
                <label for="paymentTerm" class="block text-sm font-medium text-gray-700">Payment Term (days)</label>
                <select id="paymentTerm" v-model="paymentTerm" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </div>
          </div>

          <!-- EEC Fees -->
          <div class="space-y-6">
            <div>
              <h2 class="text-lg font-medium text-gray-900">EEC Fees (Early Education Centre)</h2>
              <p class="mt-1 text-sm text-gray-500">Configure fees for Early Education Centre programs by pricing tier.</p>
            </div>

            <!-- Preschool (PRE) - Only 5 full days -->
            <div class="space-y-4">
              <h3 class="text-md font-medium text-gray-700">Preschool (PRE) - 5 Full Days</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.preschool5FullDays.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.preschool5FullDays.nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.preschool5FullDays.citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>

            <!-- Junior Preschool (JPRE) - 5 Full Days -->
            <div class="space-y-4">
              <h3 class="text-md font-medium text-gray-700">Junior Preschool (JPRE) - 5 Full Days</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5FullDays.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5FullDays.nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5FullDays.citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>

            <!-- Junior Preschool (JPRE) - 5 Mornings -->
            <div class="space-y-4">
              <h3 class="text-md font-medium text-gray-700">Junior Preschool (JPRE) - 5 Mornings</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5Mornings.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5Mornings.nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool5Mornings.citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>

            <!-- Junior Preschool (JPRE) - 3 Mornings -->
            <div class="space-y-4">
              <h3 class="text-md font-medium text-gray-700">Junior Preschool (JPRE) - 3 Mornings</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool3Mornings.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool3Mornings.nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" v-model="fees.eec.juniorPreschool3Mornings.citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- School Level Fees -->
          <div class="space-y-6">
            <div>
              <h2 class="text-lg font-medium text-gray-900">School Level Fees</h2>
              <p class="mt-1 text-sm text-gray-500">Configure fees for different school levels and student categories.</p>
            </div>
            <div v-for="level in ['junior', 'middle', 'senior']" :key="level" class="space-y-4">
              <h3 class="text-md font-medium text-gray-700 capitalize">{{ level }} School</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label :for="level + 'Corporate'" class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" :id="level + 'Corporate'" v-model="fees[level].corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label :for="level + 'NonCitizen'" class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" :id="level + 'NonCitizen'" v-model="fees[level].nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label :for="level + 'Citizen'" class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" :id="level + 'Citizen'" v-model="fees[level].citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Fees -->
          <div class="space-y-6">
            <div>
              <h2 class="text-lg font-medium text-gray-900">Additional Fees</h2>
              <p class="mt-1 text-sm text-gray-500">Configure additional fees and levies.</p>
            </div>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="saceLevy1" class="block text-sm font-medium text-gray-700">SACE Levy 1</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">VUV</span>
                  </div>
                  <input type="number" id="saceLevy1" v-model="fees.saceLevy.levy1" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                </div>
              </div>
              <div>
                <label for="saceLevy2" class="block text-sm font-medium text-gray-700">SACE Levy 2</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">VUV</span>
                  </div>
                  <input type="number" id="saceLevy2" v-model="fees.saceLevy.levy2" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                </div>
              </div>
              <div>
                <label for="buildingLevy" class="block text-sm font-medium text-gray-700">Building Levy (Corporate)</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">VUV</span>
                  </div>
                  <input type="number" id="buildingLevy" v-model="fees.buildingLevy.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                </div>
              </div>
            </div>
            <!-- Little Explorer Fees (per term) -->
            <div class="space-y-4 mt-6">
              <h3 class="text-md font-medium text-gray-700">Little Explorer Program (per term)</h3>
              <div class="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label for="littleExplorerCorporate" class="block text-sm font-medium text-gray-700">Corporate</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" id="littleExplorerCorporate" v-model="fees.littleExplorer.corporate" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label for="littleExplorerNonCitizen" class="block text-sm font-medium text-gray-700">Non-Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" id="littleExplorerNonCitizen" v-model="fees.littleExplorer.nonCitizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
                <div>
                  <label for="littleExplorerCitizen" class="block text-sm font-medium text-gray-700">Citizen</label>
                  <div class="mt-1 relative rounded-md shadow-sm">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span class="text-gray-500 sm:text-sm">VUV</span>
                    </div>
                    <input type="number" id="littleExplorerCitizen" v-model="fees.littleExplorer.citizen" class="block w-full pl-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Discounts -->
          <div class="space-y-6">
            <div>
              <h2 class="text-lg font-medium text-gray-900">Discounts</h2>
              <p class="mt-1 text-sm text-gray-500">Configure discount percentages (enter as decimals, e.g., 0.5 for 50%).</p>
            </div>
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label for="staffDiscountOneParent" class="block text-sm font-medium text-gray-700">Staff Discount (1 Parent) %</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input type="number" id="staffDiscountOneParent" v-model="fees.discounts.staffDiscountOneParent" step="0.01" min="0" max="1" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
              <div>
                <label for="staffDiscountTwoParents" class="block text-sm font-medium text-gray-700">Staff Discount (2 Parents) %</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input type="number" id="staffDiscountTwoParents" v-model="fees.discounts.staffDiscountTwoParents" step="0.01" min="0" max="1" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
              <div>
                <label for="bursaryDiscount" class="block text-sm font-medium text-gray-700">Bursary Discount %</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input type="number" id="bursaryDiscount" v-model="fees.discounts.bursaryDiscount" step="0.01" min="0" max="1" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
              <div>
                <label for="scholarshipDiscount" class="block text-sm font-medium text-gray-700">Scholarship Discount %</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input type="number" id="scholarshipDiscount" v-model="fees.discounts.scholarshipDiscount" step="0.01" min="0" max="1" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
              <div>
                <label for="familySizeDiscount" class="block text-sm font-medium text-gray-700">Family Size Discount (4+) %</label>
                <div class="mt-1 relative rounded-md shadow-sm">
                  <input type="number" id="familySizeDiscount" v-model="fees.discounts.familySize" step="0.01" min="0" max="1" class="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span class="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3">
            <button type="button" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Cancel
            </button>
            <button type="submit" class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Save Settings
            </button>
          </div>
        </form>

        <!-- Fee Simulator Section -->
        <FeeSimulator :fees="fees" v-if="fees" />

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import FeeSimulator from '../components/FeeSimulator.vue';

const paymentTerm = ref('');
const billingYear = ref('');
const isLoading = ref(true);
const loadError = ref('');
const saveMessage = ref('');
const fees = ref({
  eec: {
    preschool5FullDays: {
      corporate: 0,
      nonCitizen: 0,
      citizen: 0
    },
    juniorPreschool5FullDays: {
      corporate: 0,
      nonCitizen: 0,
      citizen: 0
    },
    juniorPreschool5Mornings: {
      corporate: 0,
      nonCitizen: 0,
      citizen: 0
    },
    juniorPreschool3Mornings: {
      corporate: 0,
      nonCitizen: 0,
      citizen: 0
    }
  },
  junior: {
    corporate: 0,
    nonCitizen: 0,
    citizen: 0
  },
  middle: {
    corporate: 0,
    nonCitizen: 0,
    citizen: 0
  },
  senior: {
    corporate: 0,
    nonCitizen: 0,
    citizen: 0
  },
  saceLevy: {
    levy1: 0,
    levy2: 0
  },
  buildingLevy: {
    corporate: 0
  },
  littleExplorer: {
    corporate: 0,
    nonCitizen: 0,
    citizen: 0
  },
  discounts: {
    staffDiscountOneParent: 0,
    staffDiscountTwoParents: 0,
    familySize: 0,
    bursaryDiscount: 0,
    scholarshipDiscount: 0
  }
});

const submitForm = async () => {
  saveMessage.value = '';
  const settingsData = {
    paymentTerm: paymentTerm.value,
    billingYear: billingYear.value,
    fees: fees.value
  };

  try {
    console.log('Saving settings:', settingsData);
    const response = await axios.post('/api/updateSettings', settingsData);
    console.log(response.data.message);
    saveMessage.value = 'Settings saved successfully!';
    setTimeout(() => { saveMessage.value = ''; }, 3000);
  } catch (error) {
    console.error('Error updating settings:', error);
    saveMessage.value = `Error saving settings: ${error.response?.data?.error || error.message}`;
  }
};

// Default EEC fee structure for migration
const defaultEecFees = {
  preschool5FullDays: { corporate: 653412, nonCitizen: 653412, citizen: 653412 },
  juniorPreschool5FullDays: { corporate: 653412, nonCitizen: 653412, citizen: 653412 },
  juniorPreschool5Mornings: { corporate: 471720, nonCitizen: 471720, citizen: 471720 },
  juniorPreschool3Mornings: { corporate: 356649, nonCitizen: 356649, citizen: 356649 }
};

const fetchSettings = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    console.log('Fetching settings...');
    const response = await axios.get('/api/getSettings');
    console.log('Settings response:', response.data);
    if (response.data) {
      paymentTerm.value = response.data.paymentTerm || '';
      billingYear.value = response.data.billingYear || new Date().getFullYear();

      // Deep merge fetched fees with defaults to handle missing nested properties
      const fetchedFees = response.data.fees || {};
      console.log('Fetched fees:', fetchedFees);

      // Handle EEC fees - check if it has new structure or old structure
      if (fetchedFees.eec) {
        // Check if it's the new nested structure (has preschool5FullDays with corporate property)
        if (fetchedFees.eec.preschool5FullDays?.corporate !== undefined) {
          // New structure - use as-is but ensure all fields exist
          fees.value.eec = {
            preschool5FullDays: { ...defaultEecFees.preschool5FullDays, ...fetchedFees.eec.preschool5FullDays },
            juniorPreschool5FullDays: { ...defaultEecFees.juniorPreschool5FullDays, ...fetchedFees.eec.juniorPreschool5FullDays },
            juniorPreschool5Mornings: { ...defaultEecFees.juniorPreschool5Mornings, ...fetchedFees.eec.juniorPreschool5Mornings },
            juniorPreschool3Mornings: { ...defaultEecFees.juniorPreschool3Mornings, ...fetchedFees.eec.juniorPreschool3Mornings }
          };
        } else {
          // Old flat structure - migrate to new structure
          const oldFullElp = fetchedFees.eec.fullElp || 653412;
          const oldFiveMornings = fetchedFees.eec.fiveMornings || 471720;
          const oldThreeMornings = fetchedFees.eec.threeMornings || 356649;

          fees.value.eec = {
            preschool5FullDays: { corporate: oldFullElp, nonCitizen: oldFullElp, citizen: oldFullElp },
            juniorPreschool5FullDays: { corporate: oldFullElp, nonCitizen: oldFullElp, citizen: oldFullElp },
            juniorPreschool5Mornings: { corporate: oldFiveMornings, nonCitizen: oldFiveMornings, citizen: oldFiveMornings },
            juniorPreschool3Mornings: { corporate: oldThreeMornings, nonCitizen: oldThreeMornings, citizen: oldThreeMornings }
          };
        }
      }

      // Merge other fee sections (junior, middle, senior, etc.)
      if (fetchedFees.junior) fees.value.junior = { ...fees.value.junior, ...fetchedFees.junior };
      if (fetchedFees.middle) fees.value.middle = { ...fees.value.middle, ...fetchedFees.middle };
      if (fetchedFees.senior) fees.value.senior = { ...fees.value.senior, ...fetchedFees.senior };
      if (fetchedFees.saceLevy) fees.value.saceLevy = { ...fees.value.saceLevy, ...fetchedFees.saceLevy };
      if (fetchedFees.buildingLevy) fees.value.buildingLevy = { ...fees.value.buildingLevy, ...fetchedFees.buildingLevy };
      // Handle Little Explorer - migrate from old perTerm format to new tiered format
      if (fetchedFees.littleExplorer) {
        if (typeof fetchedFees.littleExplorer.perTerm === 'number') {
          // Old format - use same value for all tiers
          const oldValue = fetchedFees.littleExplorer.perTerm;
          fees.value.littleExplorer = {
            corporate: oldValue,
            nonCitizen: oldValue,
            citizen: oldValue
          };
        } else {
          // New tiered format
          fees.value.littleExplorer = { ...fees.value.littleExplorer, ...fetchedFees.littleExplorer };
        }
      }
      if (fetchedFees.discounts) fees.value.discounts = { ...fees.value.discounts, ...fetchedFees.discounts };

      console.log('Final fees state:', fees.value);
    } else {
      loadError.value = 'No settings data returned from server';
    }
  } catch (error) {
    console.error('Error fetching settings:', error);
    loadError.value = `Failed to load settings: ${error.message}`;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchSettings);
</script>